/**
 * The OLT credentials a SmartOLT migration asks for, as the review edits them.
 *
 * SmartOLT's API never returns an OLT's login or SNMP communities, so an OLT
 * it brings over cannot be read by this platform until somebody types them
 * (operator, 2026-09-25: "a smartolt migration should include OLT and its
 * credentials so it can pull data not available via smartolt api"). Pure, so
 * what Apply sends is pinned without a DOM.
 */
import type { OltCredentialInput, SmartoltMigrationItem } from '@simpleacs/shared';

export interface OltCredentialDraft {
  cliTransport: 'telnet' | 'ssh';
  cliPort: string;
  cliUsername: string;
  cliPassword: string;
  snmpCommunity: string;
  snmpCommunityRw: string;
}

/** A blank draft for one OLT, its ports prefilled from what SmartOLT (or this platform) holds. */
export function blankCredentialDraft(item: SmartoltMigrationItem): OltCredentialDraft {
  return {
    cliTransport: 'telnet',
    cliPort: String(item.olt?.cliPort ?? 23),
    cliUsername: '',
    cliPassword: '',
    snmpCommunity: '',
    snmpCommunityRw: '',
  };
}

/** Enough to store: a login and a read community. The write community is optional. */
export function credentialDraftReady(draft: OltCredentialDraft | undefined): boolean {
  return Boolean(draft && draft.cliUsername.trim() && draft.cliPassword && draft.snmpCommunity.trim());
}

/** Something was typed but not enough to store — said beside the OLT, and Apply waits. */
export function credentialDraftPartial(draft: OltCredentialDraft | undefined): boolean {
  if (!draft) return false;
  const typed = [draft.cliUsername.trim(), draft.cliPassword, draft.snmpCommunity.trim(), draft.snmpCommunityRw.trim()].some(Boolean);
  return typed && !credentialDraftReady(draft);
}

/** The body for one OLT, as `POST /olts/credential-check` and the apply's `oltCredentials` take it. */
export function credentialBody(draft: OltCredentialDraft, item: SmartoltMigrationItem): OltCredentialInput {
  const port = Number(draft.cliPort);
  return {
    cliTransport: draft.cliTransport,
    ...(Number.isInteger(port) && port > 0 && port <= 65535 ? { cliPort: port } : {}),
    cliUsername: draft.cliUsername.trim(),
    cliPassword: draft.cliPassword,
    snmpVersion: '2c',
    ...(item.olt?.snmpPort ? { snmpPort: item.olt.snmpPort } : {}),
    snmpCommunity: draft.snmpCommunity.trim(),
    ...(draft.snmpCommunityRw.trim() ? { snmpCommunityRw: draft.snmpCommunityRw.trim() } : {}),
  };
}

/**
 * What Apply sends: every OLT the review asked about whose draft is complete,
 * keyed by the item's key. An OLT left blank is simply absent — it is created
 * without, exactly as before, and gets its credentials from Edit later.
 */
export function credentialsForApply(
  items: SmartoltMigrationItem[],
  drafts: Record<string, OltCredentialDraft>,
): Record<string, OltCredentialInput> {
  const out: Record<string, OltCredentialInput> = {};
  for (const item of items) {
    if (item.credentials !== 'needed') continue;
    const draft = drafts[item.key];
    if (draft && credentialDraftReady(draft)) out[item.key] = credentialBody(draft, item);
  }
  return out;
}
